import { useSnackbar } from 'notistack';
import { useApi } from '../../../shared/context';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { CerebroTextButton } from '../../../shared/components';
import { getResponseData } from '../../../shared/utils/axios';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { QueryKeys } from '../../../shared/hooks';
import { TermSheetFacility, TermSheetFacilityFee } from '../../../shared/models';

interface DeleteFeeDialogProps {
	dealGuid: string | undefined;
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	facility: TermSheetFacility | null;
	fee: TermSheetFacilityFee | null;
	close: () => void;
}

export const DeleteFeeDialog: React.FC<DeleteFeeDialogProps> = ({
	dealGuid,
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	facility,
	fee,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onDeleteConfirmation = () => {
		if (fee !== null) deleteFee(fee);
	};

	const { mutate: deleteFee } = useMutation<void, unknown, TermSheetFacilityFee>(
		feeToDelete =>
			getResponseData(
				api.delete<void>(
					CerebroApiEndpoints.TermSheets.SpecificFacilityFee(
						dataRoomGuid,
						termSheetGuid,
						revisionGuid,
						facility?.guid,
						feeToDelete?.guid
					)
				)
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: () => {
				client.invalidateQueries(
					QueryKeys.TermSheets.FacilityFees(dataRoomGuid, termSheetGuid, revisionGuid, facility?.guid)
				);
				client.invalidateQueries(QueryKeys.TermSheets.Specific(dealGuid, termSheetGuid));
				client.invalidateQueries(QueryKeys.TermSheets.Revision(dealGuid, termSheetGuid, revisionGuid));
				snackbar.enqueueSnackbar('Facility fee successfully deleted.', { variant: 'success' });
				close();
			},
			onError: () => {
				snackbar.enqueueSnackbar('There was a problem deleting the facility fee.', {
					variant: 'error',
				});
			},
		}
	);

	return (
		<Dialog open={fee !== null} onClose={close}>
			<DialogTitle>Confirm Delete</DialogTitle>
			<DialogContent>
				<DialogContentText>Are you sure you want to delete the facility fee?</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onDeleteConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
